import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { OnboardingDraftStep } from "@graftus/schema";
import { resolveScreen } from "@/lib/onboarding/step-machine";
import {
  ONBOARDING_SCREENS,
  ONBOARDING_STEPS,
  screenIndex,
  stepIndex,
  type OnboardingScreen,
  type OnboardingStepKey,
} from "@/types/onboarding";

interface UseOnboardingStepOpts {
  hasUser: boolean;
  hasBusiness: boolean;
  hasLocalSector: boolean;
  draftStep: OnboardingDraftStep | null;
  finished: boolean;
  /** When false, the hook neither clamps nor rewrites the URL (state still loading). */
  ready: boolean;
  devUnlock?: boolean;
}

export type RailItemState = "done" | "current" | "upcoming";

export interface RailItem {
  key: OnboardingStepKey;
  state: RailItemState;
  clickable: boolean;
}

/**
 * The URL (`?s=`) is the single source of truth for the current screen; the
 * step machine clamps it to what the state allows and rewrites it (with
 * `replace`) once `ready`. Going back is a push so the browser back button
 * works; going forward past the reachable step is refused.
 */
export function useOnboardingStep(opts: UseOnboardingStepOpts) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("s");

  const resolved = useMemo(
    () =>
      resolveScreen({
        requested,
        hasUser: opts.hasUser,
        hasBusiness: opts.hasBusiness,
        hasLocalSector: opts.hasLocalSector,
        draftStep: opts.draftStep,
        finished: opts.finished,
        devUnlock: opts.devUnlock,
      }),
    [
      requested,
      opts.hasUser,
      opts.hasBusiness,
      opts.hasLocalSector,
      opts.draftStep,
      opts.finished,
      opts.devUnlock,
    ],
  );
  const { screen, min, max } = resolved;

  useEffect(() => {
    if (!opts.ready) return;
    const absentWelcome = requested === null && screen === "welcome";
    if (!absentWelcome && requested !== screen) {
      setSearchParams({ s: screen }, { replace: true });
    }
  }, [opts.ready, requested, screen, setSearchParams]);

  const canGoTo = useCallback(
    (target: OnboardingScreen) => {
      const index = screenIndex(target);
      return index >= screenIndex(min) && index <= screenIndex(max);
    },
    [min, max],
  );

  const goTo = useCallback(
    (target: OnboardingScreen) => {
      if (!canGoTo(target)) return;
      setSearchParams({ s: target }, { replace: false });
    },
    [canGoTo, setSearchParams],
  );

  const next = useCallback(() => {
    const following = ONBOARDING_SCREENS[screenIndex(screen) + 1];
    if (following) goTo(following);
  }, [screen, goTo]);

  const prev = useCallback(() => {
    const preceding = ONBOARDING_SCREENS[screenIndex(screen) - 1];
    if (preceding) goTo(preceding);
  }, [screen, goTo]);

  const current = stepIndex(screen);
  const railItems = useMemo<RailItem[]>(
    () =>
      ONBOARDING_STEPS.map((key, index) => {
        const done =
          index < current || (key === "account" && opts.hasUser) || screen === "done";
        const state: RailItemState =
          !done && index === current ? "current" : done ? "done" : "upcoming";
        return { key, state, clickable: canGoTo(key) && key !== screen };
      }),
    [current, opts.hasUser, screen, canGoTo],
  );

  return {
    screen,
    /** -1 on the welcome, 0..5 on a step, 6 on the done screen. */
    stepIndex: current,
    min,
    max,
    canGoTo,
    goTo,
    next,
    prev,
    railItems,
  };
}
